import React, { RefObject, useEffect, useRef, useState } from 'react';
import { translate } from 'components/Translation';
import Accordion from 'components/Accordion/Accordion';
import Wrapper from 'components/Wrapper/Wrapper';
import AlternativeSelect from 'components/AlternativeSelect/AlternativeSelect';
import Business from '@material-ui/icons/Business';
import { AccountsService } from 'services/accounts/accounts';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { finalize } from 'rxjs/operators';
import styles from 'assets/jss/material-dashboard-pro-react/views/services/servicesStyle';
import { makeStyles } from '@material-ui/core';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { HeadingMBold, HeadingS, HeadingSBold } from 'components/Typography/Typography';
import AccordionContent from 'views/AddNewServices/ServicesList/subcomponents/AccordionContent';
import { IAlternativeSelectItem } from 'components/AlternativeSelect/AlternativeSelect';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { services } from './constants';
import { setSelectedAccountDetails } from 'redux/modules/addServices/actions';
import { RiyadaCustomerIdentifier } from 'services/riyadaCustomerIdentifier';
import { TagManager } from 'commons/gtm/tagManager';
import { getLanguage } from 'utils/language/language';

// @ts-ignore
const useStyles = makeStyles(styles);

const ServicesList = () => {
	const [accounts, setAccounts] = useState<IAlternativeSelectItem[]>([]);
	const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
	const [selectedAccount, setSelectedAccount] = useState<IAlternativeSelectItem | null>(null);
	const [isRiyadaCustomer, setIsRiyadaCustomer] = useState(false);
	const dispatch = useDispatch();
	const classes = useStyles();
	const isMounted = useRef(true);
	const isEnterprise: boolean = accounts.length > 1;
	// this component get focussed - for accessibility
	const ref: RefObject<HTMLDivElement> = useRef(null);
	useEffect(() => {
		if (ref.current) ref.current.focus();
	}, []);
	useEffect(() => {
		dispatch(showLoadingIndicator());
		AccountsService.getCorpAccountsForCurrentUser()
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					if (isMounted.current) {
						setLoadingAccounts(false);
					}
				})
			)
			.subscribe(({ accounts }) => {
				if (isMounted.current) {
					setAccounts(
						accounts.map((account) => ({
							id: account.accountId,
							name: account.accountName,
							documentId: account.documentId,
						}))
					);
				}
			});
	}, [dispatch]);

	useEffect(() => {
		if (accounts && accounts.length > 0) {
			setSelectedAccount(accounts[0]);
			dispatch(setSelectedAccountDetails(accounts[0]));
		}
	}, [accounts]);

	useEffect(() => {
		if (selectedAccount?.documentId) {
			dispatch(showLoadingIndicator());
			RiyadaCustomerIdentifier.isRiyadaCustomer(selectedAccount.documentId).subscribe(
				({ response }) => {
					dispatch(hideLoadingIndicator());
					setIsRiyadaCustomer(response?.data?.riyadaCustomer);
				},
				(error) => dispatch(hideLoadingIndicator())
			);
		}
	}, [selectedAccount]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: 'add new services',
				language: getLanguage(),
			},
		});
	}, []);

	return (
		<ConditionalRender
			show={!loadingAccounts}
			onTrue={() => (
				<div ref={ref}>
					<ConditionalRender
						show={isEnterprise}
						onTrue={() => (
							<GridContainer justify="flex-start">
								<GridItem xs={12} sm={6} lg={3}>
									<AlternativeSelect
										key="accountSelect"
										type="md"
										value={
											<div className={classes.selectedItem}>
												<CustomAvatar
													accountId={selectedAccount?.id}
													alt="Company Logo"
													placeholderIcon={<Business />}
													customClasses={classes.selectedItemIcon}
												/>
												<HeadingSBold color="primary-light">
													{selectedAccount ? selectedAccount.name : translate('all.accounts')}
												</HeadingSBold>
											</div>
										}
										onClick={(item) => {
											setSelectedAccount(item);
											dispatch(setSelectedAccountDetails(item));
										}}
										items={accounts}
										customIcons
										selectedItems={selectedAccount ? [selectedAccount.id] : []}
									/>
								</GridItem>
							</GridContainer>
						)}
					/>
					<div className={classes.headingWrapper}>
						<h1 className={classes.heading}>
							<HeadingMBold color="secondary-light" disableMargin asSpan>
								{translate('add.new.services')}
							</HeadingMBold>
						</h1>
					</div>
					<div className={classes.accordionWrapper}>
						<Accordion
							multiple
							active={[0, 1, 2]}
							collapses={services(isRiyadaCustomer)
								.filter(({ visible }) => visible)
								.map((service) => ({
									id: service.id,
									icon: service.icon,
									iconBackgroundColor: service.iconBackgroundColor,
									title: <HeadingS color="primary-light">{service.name}</HeadingS>,
									content: (
										<AccordionContent
											tiles={service.tiles}
											selectedAccountId={selectedAccount?.id || null}
										/>
									),
								}))}
						/>
					</div>
				</div>
			)}
		/>
	);
};

export default Wrapper(ServicesList);
